import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
    ArrowLeft,
    Mail,
    Clock,
    LifeBuoy,
    ArrowRight,
    PhoneCall,
} from "lucide-react";
import TopNav from "../components/landing/TopNav";
import Footer from "../components/landing/Footer";
import ContactForm from "../components/support/ContactForm";

const REVEAL = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
};

export default function Support() {
    const reduce = useReducedMotion();
    const { pathname } = useLocation();

    useEffect(() => {
        document.title = "Support — BetweenUs";
        window.scrollTo(0, 0);
    }, [pathname]);

    const anim = reduce ? {} : REVEAL;

    return (
        <div
            className="relative min-h-screen bg-paper text-ink"
            data-testid="support-page"
        >
            <TopNav variant="legal" />

            {/* Hero */}
            <section
                data-testid="support-hero"
                className="relative overflow-hidden border-b border-brand"
            >
                <div className="absolute inset-0 hero-wash pointer-events-none" />
                <div className="noise-overlay" />
                <div className="relative mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-14 sm:pb-20">
                    <Link
                        to="/"
                        data-testid="support-back-home"
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] transition-colors"
                    >
                        <ArrowLeft size={14} /> Back to home
                    </Link>

                    <motion.div
                        {...anim}
                        className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand bg-white/70 backdrop-blur px-3 py-1.5"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand-teal-2)]" />
                        <span className="text-[11.5px] font-semibold tracking-[0.14em] uppercase text-[var(--brand-muted-2)]">
                            Support
                        </span>
                    </motion.div>

                    <motion.h1
                        {...anim}
                        transition={{
                            ...(anim.transition || {}),
                            delay: 0.05,
                        }}
                        data-testid="support-title"
                        className="mt-5 font-display text-[40px] sm:text-[52px] lg:text-[62px] leading-[1.03] tracking-[-0.025em] text-[var(--brand-ink)]"
                    >
                        How can we{" "}
                        <span className="italic text-[var(--brand-navy)]">
                            help?
                        </span>
                    </motion.h1>

                    <motion.p
                        {...anim}
                        transition={{
                            ...(anim.transition || {}),
                            delay: 0.1,
                        }}
                        className="mt-5 max-w-[62ch] text-[16.5px] leading-relaxed text-[var(--brand-muted-2)]"
                    >
                        We're a small team and we read every message. Tell us
                        what's going on — we'll get back to you as soon as
                        we can, usually within{" "}
                        <strong className="text-[var(--brand-ink)]">
                            two business days
                        </strong>
                        .
                    </motion.p>

                    <motion.div
                        {...anim}
                        transition={{
                            ...(anim.transition || {}),
                            delay: 0.15,
                        }}
                        className="mt-8 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#contact-form"
                            data-testid="support-hero-cta"
                            className="inline-flex items-center gap-2.5 h-12 px-5 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[14px] font-semibold tracking-[-0.01em] shadow-[0_10px_24px_rgba(14,27,42,0.14)] hover:bg-[var(--brand-navy-2)] hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98] transition-[background-color,transform,box-shadow] duration-200"
                        >
                            <Mail size={16} strokeWidth={2} />
                            Send us a message
                        </a>
                        <span className="inline-flex items-center gap-2 text-[13px] text-[var(--brand-muted-2)]">
                            <Clock
                                size={14}
                                className="text-[var(--brand-teal-2)]"
                            />
                            Replies within ~48 hours on business days
                        </span>
                    </motion.div>
                </div>
            </section>

            {/* Crisis / safety banner */}
            <section
                data-testid="support-crisis-banner"
                aria-label="Safety notice"
                className="relative py-10 sm:py-12 border-b border-brand"
            >
                <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
                    <motion.div
                        {...anim}
                        className="relative overflow-hidden rounded-[22px] bg-[var(--brand-navy)] text-[var(--brand-paper)] p-6 sm:p-8 border border-[rgba(246,241,231,0.08)] shadow-[var(--shadow-soft)]"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                            <span className="inline-flex items-center justify-center h-11 w-11 rounded-[14px] bg-[rgba(246,241,231,0.12)] flex-none">
                                <LifeBuoy size={20} strokeWidth={1.8} />
                            </span>
                            <div className="flex-1">
                                <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[rgba(246,241,231,0.72)]">
                                    In crisis? BetweenUs can't help here.
                                </p>
                                <h2 className="mt-2 font-display text-[22px] sm:text-[26px] leading-snug tracking-[-0.015em]">
                                    If you or someone you know is in immediate
                                    danger, please reach out now — not by
                                    email.
                                </h2>
                                <p className="mt-3 text-[14.5px] leading-relaxed text-[rgba(246,241,231,0.84)] max-w-[58ch]">
                                    BetweenUs is a communication tool, not
                                    therapy, legal advice, or a crisis
                                    service. Support tickets may take a day
                                    or two — for anything urgent, please use
                                    the resources below.
                                </p>
                                <ul className="mt-5 grid gap-2.5 sm:grid-cols-2 text-[13.5px] leading-snug">
                                    <li className="flex items-start gap-2.5">
                                        <PhoneCall
                                            size={16}
                                            className="mt-0.5 flex-none text-[var(--brand-gold-soft)]"
                                        />
                                        <span>
                                            <strong className="block">
                                                Emergency (US)
                                            </strong>
                                            Call <a
                                                href="tel:911"
                                                className="underline decoration-[rgba(246,241,231,0.5)] underline-offset-[3px] hover:decoration-[var(--brand-paper)]"
                                            >
                                                911
                                            </a>{" "}
                                            or local emergency services
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2.5">
                                        <PhoneCall
                                            size={16}
                                            className="mt-0.5 flex-none text-[var(--brand-gold-soft)]"
                                        />
                                        <span>
                                            <strong className="block">
                                                988 Suicide & Crisis Lifeline
                                            </strong>
                                            Call or text{" "}
                                            <a
                                                href="tel:988"
                                                className="underline decoration-[rgba(246,241,231,0.5)] underline-offset-[3px] hover:decoration-[var(--brand-paper)]"
                                            >
                                                988
                                            </a>{" "}
                                            (US & Canada)
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2.5">
                                        <PhoneCall
                                            size={16}
                                            className="mt-0.5 flex-none text-[var(--brand-gold-soft)]"
                                        />
                                        <span>
                                            <strong className="block">
                                                Domestic Violence Hotline (US)
                                            </strong>
                                            Call{" "}
                                            <a
                                                href="tel:18007997233"
                                                className="underline decoration-[rgba(246,241,231,0.5)] underline-offset-[3px] hover:decoration-[var(--brand-paper)]"
                                            >
                                                1-800-799-7233
                                            </a>{" "}
                                            or text "START" to 88788
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2.5">
                                        <PhoneCall
                                            size={16}
                                            className="mt-0.5 flex-none text-[var(--brand-gold-soft)]"
                                        />
                                        <span>
                                            <strong className="block">
                                                Outside the US
                                            </strong>
                                            Visit{" "}
                                            <a
                                                href="https://findahelpline.com"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="underline decoration-[rgba(246,241,231,0.5)] underline-offset-[3px] hover:decoration-[var(--brand-paper)]"
                                            >
                                                findahelpline.com
                                            </a>
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Contact form */}
            <section
                id="contact-form"
                data-testid="support-form-section"
                className="relative py-16 sm:py-20 scroll-mt-24"
            >
                <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8">
                    <motion.div {...anim} className="max-w-[640px]">
                        <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[var(--brand-teal-2)]">
                            Contact
                        </p>
                        <h2 className="mt-3 font-display text-[30px] sm:text-[38px] lg:text-[44px] leading-[1.05] tracking-[-0.02em] text-[var(--brand-ink)]">
                            Tell us what&rsquo;s going on.
                        </h2>
                        <p className="mt-4 max-w-[58ch] text-[15.5px] leading-relaxed text-[var(--brand-muted-2)]">
                            Share a little context and the best way to reach
                            you. We&rsquo;ll reply from a real person.
                        </p>
                    </motion.div>

                    <motion.div
                        {...anim}
                        transition={{
                            ...(anim.transition || {}),
                            delay: 0.08,
                        }}
                        className="mt-8"
                    >
                        <ContactForm />
                    </motion.div>
                </div>
            </section>

            {/* Contact card + helpful links */}
            <section
                data-testid="support-contact-section"
                className="relative py-16 sm:py-20 bg-[var(--brand-paper-2)] border-y border-brand"
            >
                <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
                    <div className="grid gap-8 lg:grid-cols-12 items-start">
                        <motion.div
                            {...anim}
                            data-testid="support-contact-card"
                            className="lg:col-span-7 rounded-[24px] bg-white border border-brand p-7 sm:p-9 shadow-[var(--shadow-soft)]"
                        >
                            <div className="flex items-center gap-3">
                                <span className="inline-flex items-center justify-center h-11 w-11 rounded-[14px] bg-[rgba(26,58,108,0.08)] border border-[rgba(26,58,108,0.18)] text-[var(--brand-navy)]">
                                    <Mail size={19} strokeWidth={1.8} />
                                </span>
                                <div>
                                    <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--brand-teal-2)]">
                                        Prefer email?
                                    </p>
                                    <h2 className="font-display text-[24px] sm:text-[28px] leading-[1.1] tracking-[-0.015em] text-[var(--brand-ink)]">
                                        Write us directly.
                                    </h2>
                                </div>
                            </div>

                            <dl className="mt-6 space-y-4 text-[14.5px] leading-relaxed">
                                <div>
                                    <dt className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[var(--brand-muted-2)]">
                                        General support
                                    </dt>
                                    <dd className="mt-1">
                                        <a
                                            href="mailto:support@tylegn.com"
                                            data-testid="support-email-general"
                                            className="font-medium text-[var(--brand-navy)] hover:text-[var(--brand-teal-2)] underline decoration-[rgba(26,58,108,0.3)] hover:decoration-[var(--brand-teal-2)] underline-offset-[3px]"
                                        >
                                            support@tylegn.com
                                        </a>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[var(--brand-muted-2)]">
                                        Privacy requests
                                    </dt>
                                    <dd className="mt-1">
                                        <a
                                            href="mailto:privacy@tylegn.com"
                                            data-testid="support-email-privacy"
                                            className="font-medium text-[var(--brand-navy)] hover:text-[var(--brand-teal-2)] underline decoration-[rgba(26,58,108,0.3)] hover:decoration-[var(--brand-teal-2)] underline-offset-[3px]"
                                        >
                                            privacy@tylegn.com
                                        </a>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[var(--brand-muted-2)]">
                                        Mailing address
                                    </dt>
                                    <dd className="mt-1 text-[var(--brand-ink)]">
                                        Tylegn Gaming and Entertainment, LLC
                                        <br />
                                        PO Box 3118, Hailey, ID 83333
                                        <br />
                                        United States
                                    </dd>
                                </div>
                            </dl>

                            <p className="mt-6 text-[12.5px] text-[var(--brand-muted)] leading-relaxed">
                                To help us help you faster, include: your
                                device and OS version, the BetweenUs version,
                                and a short description of what you were
                                trying to do when it happened.
                            </p>
                        </motion.div>

                        <motion.aside
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.08,
                            }}
                            data-testid="support-helpful-links"
                            className="lg:col-span-5 rounded-[24px] bg-[var(--brand-navy)] text-[var(--brand-paper)] p-7 sm:p-9 border border-[rgba(246,241,231,0.08)] shadow-[var(--shadow-soft)]"
                        >
                            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[rgba(246,241,231,0.72)]">
                                Helpful links
                            </p>
                            <h3 className="mt-2 font-display text-[22px] leading-[1.15] tracking-[-0.015em]">
                                You might find your answer here first.
                            </h3>
                            <ul className="mt-6 space-y-3 text-[14px]">
                                <HelpfulLink
                                    to="/#faq"
                                    label="Frequently asked questions"
                                    testId="support-link-faq"
                                />
                                <HelpfulLink
                                    to="/#safety"
                                    label="Safety & privacy overview"
                                    testId="support-link-safety"
                                />
                                <HelpfulLink
                                    to="/privacy"
                                    label="Privacy Policy"
                                    testId="support-link-privacy"
                                />
                                <HelpfulLink
                                    to="/terms"
                                    label="Terms & Conditions"
                                    testId="support-link-terms"
                                />
                                <HelpfulLink
                                    to="/eula"
                                    label="End User License Agreement"
                                    testId="support-link-eula"
                                />
                            </ul>
                        </motion.aside>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function HelpfulLink({ to, label, testId }) {
    return (
        <li>
            <Link
                to={to}
                data-testid={testId}
                className="group flex items-center justify-between gap-3 rounded-[12px] px-3.5 py-2.5 bg-[rgba(246,241,231,0.08)] border border-[rgba(246,241,231,0.12)] hover:bg-[rgba(246,241,231,0.14)] hover:border-[rgba(246,241,231,0.22)] transition-colors"
            >
                <span className="font-medium text-[var(--brand-paper)]">
                    {label}
                </span>
                <ArrowRight
                    size={14}
                    className="text-[rgba(246,241,231,0.7)] group-hover:text-[var(--brand-paper)] translate-x-0 group-hover:translate-x-0.5 transition-[transform,color]"
                />
            </Link>
        </li>
    );
}
